import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { getNotifications, markNotificationsRead, resolveNotification } from "../lib/api";

const TYPE_ICON = { lead: "UserPlus", chat: "MessageSquare", rank: "Trophy", payout: "Banknote", document: "FileText", sla: "AlarmClock", system: "Info" };

export const NotificationBell = () => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const go = async (n) => {
    const m = n.meta || {};
    if (m.entity_type && m.entity_id) {
      setOpen(false);
      try {
        const r = await resolveNotification(m.entity_type, m.entity_id);
        if (r.exists) {
          navigate(`/dashboard?tab=${r.tab}&focus_type=${encodeURIComponent(r.entity_type)}&focus_id=${encodeURIComponent(r.entity_id)}`);
        } else {
          toast.info("That item is no longer available.");
          navigate(`/dashboard?tab=${r.tab || m.tab || "overview"}`);
        }
      } catch (e) {
        if (e?.response?.status === 403) toast.error("You don't have access to that item.");
        navigate(`/dashboard?tab=${m.tab || "overview"}`);
      }
    } else if (m.tab) {
      setOpen(false);
      navigate(`/dashboard?tab=${m.tab}`);
    }
  };
  const load = () => getNotifications().then(setData).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = async () => {
    const n = !open;
    setOpen(n);
    if (n && data.unread) { try { await markNotificationsRead(); } catch {} load(); }
  };
  const fmt = (t) => { try { return new Date(t).toLocaleString(); } catch { return t; } };
  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell" aria-label="Notifications"
        className="relative w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-200 transition-colors">
        <Icons.Bell className="w-5 h-5" />
        {data.unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed left-3 right-3 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 max-h-[70vh] sm:max-h-96 overflow-y-auto card-dark rounded-xl border border-white/10 shadow-2xl z-50" data-testid="notif-dropdown">
          <div className="px-4 py-3 border-b border-white/10 text-white font-semibold text-sm sticky top-0 bg-obsidian-800">Notifications</div>
          {data.notifications.length === 0 ? (
            <div className="px-4 py-8 text-slate-500 text-sm text-center">You're all caught up 🎉</div>
          ) : data.notifications.map((n) => {
            const ic = TYPE_ICON[n.type] || "Info";
            const IC = Icons[ic] || Icons.Info;
            const clickable = !!(n.meta?.tab || n.meta?.entity_id);
            return (
              <div key={n.id} onClick={() => go(n)} className={`px-4 py-3 border-b border-white/5 hover:bg-white/5 flex gap-3 ${clickable ? "cursor-pointer" : ""}`} data-testid={`notif-item-${n.id}`}>
                <span className="w-7 h-7 rounded-full bg-gold/15 flex items-center justify-center shrink-0"><IC className="w-3.5 h-3.5 text-gold" /></span>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium">{n.title}</div>
                  {n.body && <div className="text-xs text-slate-400 mt-0.5 break-words">{n.body}</div>}
                  <div className="text-[10px] text-slate-600 mt-1">{fmt(n.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
